import { publicApi } from "@api";
import { envConfig } from "@configs/env";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePreference from "@hooks/usePreference";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import {
  Button,
  CodeField,
  standardizePhoneNumber,
  useCallbackRef,
  useDateAvailCheck,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { DEFAULT_OTP_LENGTH } from "../FormOTPAndPIN/FormOTPAndPIN";
import FormTitle from "../FormTitle";
import { useOTPCountDown } from "./hooks";

interface FormOTPProps extends PropsWithChildren {
  phone: string;
  onComplete: (code: string) => void;
  OTPValue: string;
  errorMessage: string;
  loading?: boolean;
  mode: "register" | "forgot-password";
}

const FormOTP = ({ errorMessage: errMsg, OTPValue, phone, onComplete, loading, mode }: FormOTPProps) => {
  const [isDisplayCountDown, setIsDisplayCountDown] = useState(false); // hide count down on first load
  const { time: otpCountDown, countFrom } = useOTPCountDown();
  const { serverInfo } = usePreference();
  const { phonePrefix } = useMemberSettings();

  const [resendOTP, { isLoading: isResendingOTP }] =
    mode === "register" ? publicApi.useCreateRegisterMutation() : publicApi.useCreateForgotPasswordMutation();

  const { handleApiResponse } = useHandleApiResponse({ toast });
  const { t, i18n } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | undefined>();

  useEffect(() => {
    setErrorMessage(t(errMsg));
  }, [errMsg, t]);

  const {
    check: checkRequestOTP,
    dueAt: nextOTPAvailDate,
    updateDueAt: updateOTPNextAvailDate,
  } = useDateAvailCheck(standardizePhoneNumber(phone, phonePrefix));

  const handleOnOTPComplete = useCallback(
    async (code: string) => {
      onComplete(code);
    },
    [onComplete],
  );

  //#region check if key is expired or not
  useEffect(() => {
    if (nextOTPAvailDate) {
      countFrom(Math.max(nextOTPAvailDate.diff(Date.now(), "seconds"), 0));
    }
  }, [countFrom, nextOTPAvailDate]);
  //#endregion

  const formatTimeout = useCallback((timeout: number) => {
    const minutes = Math.floor(timeout / 60);
    const seconds = timeout % 60;
    return `${minutes < 10 ? `0${minutes}` : minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  }, []);

  const handleResendOTP = useCallbackRef(async () => {
    if (!checkRequestOTP()) {
      setIsDisplayCountDown(true);
      toast.error(
        t("You can request OTP again in", {
          timeout: formatTimeout(otpCountDown!),
        }),
      );
      return;
    }
    const response = await resendOTP({
      phone: standardizePhoneNumber(phone, phonePrefix),
      accountId: envConfig.accountId,
    });
    if (response.error) {
      handleApiResponse(response, {
        toastError: false,
        onError: (error) => {
          const message = resolveAuthResponseError(error, t);
          if (message) {
            toast.error(message);
          }
        },
      });
    } else {
      handleApiResponse(response, {
        toastSuccess: false,
        onSuccess: () => {
          toast.success(getOTPCodeSentMessage(response.data.method, i18n.language, t));
        },
      });
      updateOTPNextAvailDate(response.data.nextRequestAt);
    }
  });

  const isFormLoading = useMemo(() => {
    return isResendingOTP || loading;
  }, [isResendingOTP, loading]);

  return (
    <div className="flex w-full flex-col items-center">
      <FormTitle
        title={phone}
        subTitle={t("Enter the OTP sent to your phone")}
      />
      <div className="mt-6">
        <CodeField
          length={serverInfo?.onboard?.otpLength ?? DEFAULT_OTP_LENGTH}
          type={"number"}
          shouldAutoFocus
          value={OTPValue}
          onChange={handleOnOTPComplete}
          error={!!errorMessage}
          helperText={t(errorMessage ?? "")}
          helperTextClasses={{
            className: "!text-center",
          }}
          disabled={isFormLoading}
          variant="ghost"
          className="flex flex-col items-center"
        />
      </div>

      <div className="mt-6 w-full">
        <Button
          id="btn-next"
          variant="primary"
          size="lg"
          onClick={() => handleOnOTPComplete(OTPValue)}
          disabled={OTPValue == "" || OTPValue.length < (serverInfo?.onboard?.otpLength ?? DEFAULT_OTP_LENGTH)}
          loading={isFormLoading}
        >
          {t("Next")}
        </Button>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-x-2">
          {isDisplayCountDown && (
            <p>
              {!!otpCountDown && (
                <div className="flex items-center gap-x-1 gap-y-0.5 text-[10px] leading-4 text-content-soft">
                  <span>{t("Send code again in")}</span>
                  <span>{formatTimeout(otpCountDown)}</span>
                </div>
              )}
            </p>
          )}
          <Button
            size="sm"
            className="!w-fit px-3"
            variant="secondary"
            loading={isFormLoading}
            onClick={handleResendOTP}
            disabled={isDisplayCountDown && otpCountDown > 0}
          >
            {t("Resend code")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FormOTP;
